import { buildURLforProviderProcedure } from "pages/CreateOrder/utils/helpers";
import { IPayerWithTerms } from "types/payerTerms";
import {
  IProviderProcedureResponse,
  ProcedureProviderSearchBody,
  IGroupedProviderProcedureResponse,
} from "types/ProviderProcedure";

import { api } from ".";

export interface IProviderProcedureRequest {
  payerNameField?: string;
  cptCodeField?: string;
  cptDescription?: string;
  providerName?: string;
  specialty?: string;
  connectedHospital?: string;
  page?: number;
  pageSize?: number;
  accountId?: string;
  limit?: number;
  /** ISO YYYY-MM-DD — optional; backend resolves prices from the contract fee schedule active on this date. */
  feeEffectiveDate?: string;
}

export interface IProviderProcedureComparisonRequest
  extends IProviderProcedureRequest {
  specialty: string;
  onlyLowerThanMedicare?: boolean;
  volume?: number;
  volumeMatchMode?: string;
  contractFeeScheduleIds?: string[];
}

const providerProcedureQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getProviderProcedure: builder.query<
      IProviderProcedureResponse,
      IProviderProcedureRequest
    >({
      query: ({
        page,
        pageSize,
        payerNameField,
        cptCodeField,
        cptDescription,
        providerName,
        specialty,
        connectedHospital,
        feeEffectiveDate,
      }) => {
        return buildURLforProviderProcedure({
          baseUrl: "provider_procedures",
          payerNameField,
          cptCodeField,
          cptDescription,
          providerName,
          specialty,
          connectedHospital,
          page,
          pageSize,
          feeEffectiveDate,
        });
      },
    }),
    getAllProviderProcedureComparison: builder.query<
      IGroupedProviderProcedureResponse,
      IProviderProcedureComparisonRequest
    >({
      query: ({
        accountId,
        page,
        pageSize,
        payerNameField,
        cptCodeField,
        cptDescription,
        providerName,
        specialty,
        onlyLowerThanMedicare,
        volume,
        volumeMatchMode,
        contractFeeScheduleIds,
      }) => {
        return buildURLforProviderProcedure({
          baseUrl: `provider_procedures/contract-management/${accountId}`,
          payerNameField,
          cptCodeField,
          cptDescription,
          providerName,
          specialty,
          page,
          pageSize,
          onlyLowerThanMedicare,
          volume,
          volumeMatchMode,
          contractFeeScheduleIds,
          defaultPagination: false,
        });
      },
    }),
    getPayerTermsForAccount: builder.query<
      IPayerWithTerms[],
      { accountId: string; specialty?: string }
    >({
      query: ({ accountId, specialty }) => {
        const search = specialty
          ? `?specialty=${encodeURIComponent(specialty)}`
          : "";
        return {
          url: `provider_procedures/contract-management/${accountId}/payer-terms${search}`,
          method: "GET",
        };
      },
      transformResponse: (response: { data: IPayerWithTerms[] }) =>
        response.data,
    }),
    getTop10ProviderProcedureComparison: builder.query<
      {
        data: {
          enriched: {
            procedureId: number;
            cptCode: string;
            description: string;
            orderCount: number;
            prices?: {
              payerId: number;
              payerName?: string;
              priceInCents: number;
              contractFeeScheduleEffectiveDate?: string | null;
            }[];
          }[];
          overallVarianceAverages: {
            highestVsLowestCommercialAvg: number;
            highestVsMedicareAvg: number;
            highestVsMedicaidAvg: number;
          };
        };
      },
      { accountId: string; specialty?: string; limit?: number }
    >({
      query: ({ accountId, specialty, limit }) => {
        const parts: string[] = [];
        if (specialty) {
          parts.push(`specialty=${encodeURIComponent(specialty)}`);
        }
        if (limit !== undefined) {
          parts.push(`limit=${limit}`);
        }
        const query = parts.length ? `?${parts.join("&")}` : "";

        return {
          url: `provider_procedures/contract-management/top10/${accountId}${query}`,
          method: "GET",
        };
      },
    }),
    getClearProviderProcedureByProvider: builder.mutation<
      IProviderProcedureResponse,
      { providerId: string }
    >({
      query: ({ providerId }) => ({
        url: `provider_procedures/provider/${providerId}`,
        method: "POST",
      }),
    }),
    deleteProviderProcedure: builder.mutation<any, number>({
      query: (id) => ({
        url: `provider_procedures/${id}`,
        method: "DELETE",
      }),
    }),
    deleteAllProviderProcedure: builder.mutation<any, any>({
      query: (filters) => ({
        url: `provider_procedures/`,
        method: "DELETE",
        body: {
          ...filters,
        },
      }),
    }),
    updateProviderProcedurePrice: builder.mutation<
      any,
      {
        id: number;
        priceInCents?: number;
        implantCostInCents?: number | null;
        toBeEdited?: boolean;
        requiresAbn?: boolean;
        preventive?: boolean;
        drgAverageLengthOfStay?: number | null;
        drgLosOutlierThreshold?: number | null;
        drgOutlierMultiplier?: number | null;
      }
    >({
      query: ({
        id,
        priceInCents,
        implantCostInCents,
        toBeEdited,
        requiresAbn,
        preventive,
        drgAverageLengthOfStay,
        drgLosOutlierThreshold,
        drgOutlierMultiplier,
      }) => ({
        url: `provider_procedures/${id}`,
        method: "PUT",
        body: {
          priceInCents,
          implantCostInCents,
          toBeEdited,
          requiresAbn,
          preventive,
          drgAverageLengthOfStay,
          drgLosOutlierThreshold,
          drgOutlierMultiplier,
        },
      }),
    }),
    searchProviderProcedure: builder.mutation<
      { data: IProviderProcedureResponse },
      ProcedureProviderSearchBody
    >({
      query: ({ accountId, cptCode, payerName, dateOfService }) => ({
        url: `provider_procedures/search`,
        method: "POST",
        body: {
          accountId,
          cptCode,
          payerName,
          ...(dateOfService ? { dateOfService } : {}),
        },
      }),
    }),
    getCptRecommendations: builder.mutation<
      {
        data: {
          recommendations: {
            cptCode: string;
            cptDescription: string;
            occurrenceRate: number;
            pairedWith: string[];
            existsForPayer: boolean;
          }[];
        };
        message: string;
      },
      { accountId: string; includedCptCodes: string[]; payerName?: string }
    >({
      query: ({ accountId, includedCptCodes, payerName }) => ({
        url: `provider_procedures/cpt-recommendations`,
        method: "POST",
        body: {
          accountId,
          includedCptCodes,
          payerName,
        },
      }),
    }),
  }),
});

export const {
  useGetProviderProcedureQuery,
  useGetAllProviderProcedureComparisonQuery,
  useLazyGetAllProviderProcedureComparisonQuery,
  useGetTop10ProviderProcedureComparisonQuery,
  useGetPayerTermsForAccountQuery,
  useGetClearProviderProcedureByProviderMutation,
  useDeleteProviderProcedureMutation,
  useDeleteAllProviderProcedureMutation,
  useUpdateProviderProcedurePriceMutation,
  useSearchProviderProcedureMutation,
  useGetCptRecommendationsMutation,
} = providerProcedureQueries;

export default providerProcedureQueries;
